import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Description, Launch } from "@material-ui/icons";

import { apiCall } from "../../../utils/api";
import { API_URLS } from "../../../constants/api";
import ScientificResearchUpdateModal from "../../ScientificResearch/ScientificResearchUpdateModal";

// Map ScientificResearch.type (BE enum) → filter token used by
// ScientificResearchUpdateModal to pick the correct sub-form. Conferences
// branch on conference.level for the domestic vs international split.
const resolveFilter = (row) => {
  const type = row?.scientificResearch?.type;
  switch (type) {
    case "scientificResearchJournal":
      return "journalInternational";
    case "scientificResearchJournalDomestic":
      return "journalDomestic";
    case "scientificResearchConference":
      return row?.conference?.level === "domestic" ? "conferenceDomestic" : "conference";
    case "chapterJournal":
      return "chapter";
    case "working":
      return "workingPaper";
    case "scientificResearchOther":
      return "other";
    default:
      return "journalInternational";
  }
};

// Build the `updating` object the SR modal expects: flatten scientificResearch
// fields and inject the matching publication so the publication picker in the
// sub-form has the right hint without re-fetching.
const buildUpdatingRow = (row, filter) => {
  if (!row?.scientificResearch) return null;
  let publication = null;
  if (filter === "journalInternational") publication = row.journal;
  else if (filter === "journalDomestic") publication = row.journalDomestic;
  else if (filter === "conference" || filter === "conferenceDomestic") publication = row.conference;

  return {
    ...row.scientificResearch,
    publication,
    filter,
    submissionYear: row.scientificResearch?.submissionDay
      ? new Date(row.scientificResearch.submissionDay).getFullYear()
      : undefined,
  };
};

const TYPE_LABEL = {
  scientificResearchJournal: "Tạp chí quốc tế",
  scientificResearchJournalDomestic: "Tạp chí trong nước",
  scientificResearchConference: "Hội thảo",
  chapterJournal: "Chương sách",
  working: "Working paper",
  scientificResearchOther: "Tạp chí khác",
};

function LinkedScientificResearchSection({ projectId, onCountChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [viewingFilter, setViewingFilter] = useState("journalInternational");

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      const api = API_URLS.SCIENTIFIC_RESEARCH.getScientificResearchByLinkedProjectId(projectId);
      const { response, error: err } = await apiCall({ ...api });
      if (cancelled) return;
      if (!err && response?.status === 200 && response.data?.success) {
        const data = response.data.data || [];
        setRows(data);
        if (typeof onCountChange === "function") onCountChange(data.length);
      } else {
        setError("Không tải được danh sách bài báo liên kết.");
        setRows([]);
        if (typeof onCountChange === "function") onCountChange(0);
      }
      setLoading(false);
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleOpenDetail = (row) => {
    const filter = resolveFilter(row);
    const updating = buildUpdatingRow(row, filter);
    if (!updating) return;
    setViewingFilter(filter);
    setViewing(updating);
  };

  const publicationTitleOf = (row) => {
    if (row?.journal?.title) return row.journal.title;
    if (row?.journalDomestic?.title) return row.journalDomestic.title;
    if (row?.conference?.title) return row.conference.title;
    return row?.scientificResearch?.journalTitle || "—";
  };

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const ay = a?.scientificResearch?.acceptedDay
          ? new Date(a.scientificResearch.acceptedDay).getFullYear()
          : 0;
        const by = b?.scientificResearch?.acceptedDay
          ? new Date(b.scientificResearch.acceptedDay).getFullYear()
          : 0;
        return by - ay;
      }),
    [rows],
  );

  return (
    <Box style={{ marginBottom: 16 }}>
      <Box display="flex" alignItems="center" style={{ marginBottom: 8, gap: 8 }}>
        <Description style={{ color: "#3f51b5" }} />
        <Typography variant="h6" style={{ fontWeight: 600, marginRight: 8 }}>
          Bài báo đã liên kết
        </Typography>
        <Chip
          size="small"
          color={rows.length > 0 ? "primary" : "default"}
          label={loading ? "…" : `${rows.length} bài`}
          style={{ fontWeight: 500 }}
        />
      </Box>
      <Divider style={{ marginBottom: 12 }} />

      {loading && (
        <Box display="flex" alignItems="center" justifyContent="center" style={{ padding: 16 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && error && (
        <Typography variant="body2" color="error" style={{ padding: 8 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && rows.length === 0 && (
        <Box
          style={{
            padding: 16,
            backgroundColor: "#fafafa",
            borderRadius: 4,
            border: "1px dashed #cfd8dc",
            textAlign: "center",
            color: "#78909c",
          }}
        >
          <Typography variant="body2">Chưa có bài báo nào được liên kết với đề tài này.</Typography>
        </Box>
      )}

      {!loading && !error && sorted.length > 0 && (
        <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sorted.map((row) => {
            const sr = row.scientificResearch || {};
            const year = sr.acceptedDay ? new Date(sr.acceptedDay).getFullYear() : "—";
            return (
              <Box
                key={sr.id}
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 6,
                  padding: 12,
                  backgroundColor: "#ffffff",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Tooltip title={sr.title || ""} placement="top-start">
                    <Typography
                      variant="subtitle2"
                      style={{
                        fontWeight: 600,
                        marginBottom: 4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {sr.title || "(Chưa có tên)"}
                    </Typography>
                  </Tooltip>
                  <Box display="flex" alignItems="center" style={{ gap: 6, flexWrap: "wrap" }}>
                    <Chip
                      size="small"
                      label={TYPE_LABEL[sr.type] || sr.type || "—"}
                      style={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 500 }}
                    />
                    <Chip
                      size="small"
                      label={`Năm: ${year}`}
                      variant="outlined"
                      style={{ fontSize: 12 }}
                    />
                    <Chip
                      size="small"
                      label={sr.accepted ? "Đã duyệt" : "Chưa duyệt"}
                      style={{
                        backgroundColor: sr.accepted ? "#e8f5e9" : "#ffebee",
                        color: sr.accepted ? "#2e7d32" : "#c62828",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: "block", marginTop: 4 }}
                  >
                    {publicationTitleOf(row)}
                    {sr.listAuthor?.length > 0 ? ` • ${sr.listAuthor.join(", ")}` : ""}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  startIcon={<Launch fontSize="small" />}
                  onClick={() => handleOpenDetail(row)}
                  style={{ flexShrink: 0 }}
                >
                  Chi tiết
                </Button>
              </Box>
            );
          })}
        </Box>
      )}

      <ScientificResearchUpdateModal
        readOnly
        filter={viewingFilter}
        updating={viewing}
        setUpdating={setViewing}
      />
    </Box>
  );
}

export default LinkedScientificResearchSection;
